import { Box, Card, CardHeader, Divider, Grid, Link, Chip, Table, TableBody, TableCell, TableRow, Typography, Button, 
  Stack, List, ListItem, ListItemButton, ListItemText, 
  Tooltip} from "@mui/material";
import { ListContextProvider, ReduxState, ResourceComponentPropsWithId, ShowBase, useGetOne, 
  useListContext, useListController, useShowContext } from "ra-core";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouteLink } from "react-router-dom";
import Scrollbar from "@/components/Scrollbar";
import { Content } from "@/components/content/Content";
import { FunctionField, TextField } from "@/components/field";
import DateField2 from "@/components/field/DateField2";
import SizeTextField from "@/components/field/SizeTextField";
import ClassificationLabel from "@/components/governance/labels/ClassificationLabel";
import { Datagrid } from "@/components/list/datagrid";
import { RecPatch } from "@/dataProvider/tools";
import { DataOwnerGroupByFileId, FileInstanceResponseDto, FileResponseDto, FileTagResponseDto } from "@/gen/api/securefile";
import { OdResourcePageProps } from "@/types/resource";
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid";
import { Pagination as DefaultPagination } from '@/components/list/pagination';
import { FileCataAmbientDataContextProvider, useFileCataAmbientDataContext } from "@/components/securefiles/FileCataAmbientDataProvider";
import { useDispatch, useSelector } from "react-redux";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useSnackbar } from "notistack";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { crudGetManyEx } from "@/actions/crudFetchEx";


const dataAccessRoleGrantsRes = 'settings/governance/classification/data-access-role/grants'

const FileDetail: FC<ResourceComponentPropsWithId> = ({
  id: hId,
  resource,
  options,
  history,
}) => {
  useResourceRegister({ name: 'secure-files-instances' })
  const id = decodeHashid(hId || '')
  const { enqueueSnackbar } = useSnackbar()
  const onFailure = useCallback((err: any) => {
    const errMsg = retrieveAxiosErr(err)
    enqueueSnackbar(`Fetch File Detail failed: ${errMsg}`, { variant: 'error' })
    if (history && history.length > 0) {
      history.goBack()
    }
  }, [enqueueSnackbar, history])
  
  return (
    <ShowBase resource={resource} id={id} basePath="" {...{onFailure}}>
      <ListContextProvider value={useListController<FileInstanceResponseDto&RecPatch>({ resource: id ? 'secure-files-instances' : 'noop', basePath: '', filter: {id}})}>
        <FileCataAmbientDataContextProvider>
          <FileDetailBody {...{id, resource, options}} />
        </FileCataAmbientDataContextProvider>
      </ListContextProvider>
    </ShowBase>
  )
}

const FileDetailBody: FC<OdResourcePageProps> = ({
  options,
}) => {
  const { record, refetch } = useShowContext<FileResponseDto&RecPatch>()
  const { classifications, tags } = useFileCataAmbientDataContext()
  const resReady = useSelector<ReduxState, boolean>(store => !!store.admin.resources[dataAccessRoleGrantsRes])
  const { data: grants } = useGetOne(resReady && record && record.secondaryClassificationId ? dataAccessRoleGrantsRes : 'noop', record?.secondaryClassificationId??0)
  const [clss, fileTags] = useMemo(() => {
    const clss = classifications.find(c => c.secondaryClassificationId === record?.secondaryClassificationId)
    const fileTags : FileTagResponseDto[] = (record?.tags ?? []).map(tid => tags.find(t => t.tagId === tid)).filter(x => !!x) as any
    return [clss, fileTags] as const
  }, [record, classifications, tags])
  const listCtx = useListContext()
  const { total, setPage, setFilters, filterValues } = listCtx
  const setPageRef = useRef(setPage)
  setPageRef.current = setPage
  useEffect(() => {
    setTimeout(() => setPageRef.current(1))
  }, [record?.fileId])
  const dataownergroupName = usePrepareDataOwnerGroupData(record?.fileId ?? -1)

  const [showDelete, setShowDelete] = useState(false)
  const toggleShowDeleted = () => {
    let showDel = false
    setShowDelete(v => {
      showDel = !v
      return showDel
    })
    setFilters({...filterValues, showDelete: showDel}, {})
  }

  const [showDar, setShowDar] = useState(false)
  const toggleShowDar = () => {
    let showDataAccessRole = false
    setShowDar(v => {
      showDataAccessRole = !v
      return showDataAccessRole
    })
  }

  return (
    <Content 
      pageName="File Instances" 
      resourceName="File Instances" 
      breadcrumbs={options?.breadcrumbs}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} >
          <Card >
            <CardHeader title="Basic Info" />
            <Divider />
            <Table sx={{ '& td': { py: 1.25 }, '& p.filename': { maxWidth:700, wordWrap:'break-word'}}}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: 150 }}>
                    <Typography variant="subtitle2">File Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" className="filename">
                      {record?.fileName}
                      {record?.isDeleted && <Chip sx={{ml: 4}} label="Deleted" size="small" color="error" variant="outlined" />}
                    </Typography>
                  </TableCell>
                </TableRow>
                {record?.parentFileName &&
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Parent File Name</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" className="filename">
                        <Link underline="none"
                          component={RouteLink}
                          to={genUrlWithHashid('/secure-files/$0/show', record?.parentFileId ?? 0, record?.parentFileName)}
                        >
                          {record?.parentFileName}
                        </Link>
                        {record?.parentFileDeleted && <Chip sx={{ml: 4}} label="Deleted" size="small" color="error" variant="outlined" />}
                      </Typography>
                    </TableCell>
                  </TableRow>}
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">Instances</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{total ?? 0}</Typography>
                  </TableCell>
                </TableRow>
                {Boolean(clss) && 
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Classification</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction={"row"}>
                        <Box sx={{ width:'50%' }}>
                          <ClassificationLabel variant={clss?.primaryClassification!} text={clss?.name!} />
                        </Box>
                        {Boolean(clss && grants) &&
                        <Button sx={{ml:-1}} variant="text" onClick={toggleShowDar}>{showDar ? "Hide " : "Show "}Data Access Roles with Read/Write Access</Button>}                        
                      </Stack>
                    </TableCell>
                  </TableRow>}
                {dataownergroupName &&
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Data Owner Group</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{dataownergroupName}</Typography>
                    </TableCell>
                  </TableRow>}
                {Boolean(clss && grants && showDar) && 
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2"></Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction={"row"}>
                        <Box sx={{width: '50%'}}>
                          <Typography variant="subtitle2">Data Access Roles with Read-Only Access:</Typography>
                          <List sx={{mr: 2, maxHeight: 180, overflowY: 'auto'}}>
                            {grants && grants.readDataAccessRoles.map((r:any) => 
                              <ListItem key={r.name} disablePadding>
                                <ListItemButton sx={{pt: 0.5, pb: 0.5, pl: 1, pr: 1}}
                                component={RouteLink}
                                target="_blank"
                                to={{
                                  pathname: genUrlWithHashid(`/settings/governance/data-access-role/$0/show`, r?.id, r?.name)
                                }}
                                >
                                  <Tooltip title={r.name.length > 50 ? r.name : ''}>
                                    <ListItemText secondary={r.name} sx={{maxWidth: 410, wordBreak: 'keep-all', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} />
                                  </Tooltip>
                                </ListItemButton>
                            </ListItem>)}
                          </List>
                        </Box>
                        <Box sx={{width: '50%'}}>
                          <Typography variant="subtitle2">Data Access Roles with Read & Write Access:</Typography>
                          <List sx={{mr: 2, maxHeight: 180, overflowY: 'auto'}}>
                            {grants && grants.writeDataAccessRoles.map((r:any) => 
                              <ListItem key={r.name} disablePadding>
                                <ListItemButton sx={{pt: 0.5, pb: 0.5, pl: 1, pr: 1}}
                                component={RouteLink}
                                target="_blank"
                                to={{
                                  pathname: genUrlWithHashid(`/settings/governance/data-access-role/$0/show`, r?.id, r?.name)
                                }}
                                >
                                  <Tooltip title={r.name.length > 50 ? r.name : ''}>
                                    <ListItemText secondary={r.name} sx={{maxWidth: 410, wordBreak: 'keep-all', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} />
                                  </Tooltip>
                                </ListItemButton>
                            </ListItem>)}
                          </List></Box>
                      </Stack>
                    </TableCell>
                  </TableRow>}
                {Boolean(fileTags.length) &&
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box>
                        <Typography variant="subtitle2">Tags</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ '& p': {mb: 0.5}}}>
                        {fileTags.map(t => 
                          <Typography key={t.tagId!} variant="caption" fontStyle="italic" paragraph>{t.name}</Typography>)}
                      </Box>
                    </TableCell>
                  </TableRow>}
                {record?.documentType &&
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box>
                        <Typography variant="subtitle2">Document Type</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ '& p': {mb: 0.5}}}>
                        <Chip label={record.documentType} color="success" variant="outlined" />
                      </Box>
                    </TableCell>
                  </TableRow>}
                {record?.infoTypes && record.infoTypes.length > 0 &&
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box>
                        <Typography variant="subtitle2">Info Types</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" spacing={2} 
                        sx={{ 
                          ml: -2, mt: -0.5,
                          '& p': {mb: 0.5},
                          '& div.MuiChip-root': { mt: '4px!important'},
                          '& div.MuiChip-root:nth-of-type(1)': { ml: 2 },
                        }}
                      >
                        {record.infoTypes.map((x, i) => (
                          <Chip label={x} size="small" color="secondary" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item xs={12} >
          <Card>
            <CardHeader title="Instances" action={
              <Button size="small" onClick={toggleShowDeleted}>
                {showDelete ? "Hide deleted" : "Show deleted"}
              </Button>
            } />
            <Divider />
            <Scrollbar options={{ suppressScrollY: true }}>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  {...listCtx}
                >
                  <TextField label="User" source="userName" sortable={false}/>
                  <TextField label="Computer" source="machine" sortable={false}/>
                  <FunctionField label="Location" source="path" sortable={false}
                    render={(d?:FileInstanceResponseDto&RecPatch) => 
                      <Typography variant="caption" color="secondary" sx={{maxWidth:450,display:'inline-block',wordWrap:'break-word'}}>
                        <code>{d?.path}</code>
                        {d?.isDeleted && <Chip sx={{ml: 4}} label="Deleted" size="small" color="error" variant="outlined"/>}
                      </Typography>
                    }
                  />
                  <SizeTextField label="Size" source="size" numericSource="size" emptyText="N/A" sortable={false}/>
                  <DateField2 source="lastModified" label="Last Modified Time" showTime sortable={false}/>
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore
              <DefaultPagination {...listCtx} />}
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default FileDetail;

function usePrepareDataOwnerGroupData (fileId: number) {
  const resGetMny = 'secure-files/file/data-owner-group'
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(crudGetManyEx(resGetMny, [fileId]))
  }, [dispatch, fileId])

  return useSelector<ReduxState>(store => {
    const dog : DataOwnerGroupByFileId|undefined = store.admin.resources[resGetMny]?.data[fileId] as any
    return dog?.name
  })
}
